import * as turf from "@turf/turf";

/**
 * DragRotateMode – a Mapbox-GL-Draw custom mode for moving and rotating a polygon.
 *
 * Features:
 * - Drag inside polygon to move
 * - Drag corner handles to rotate
 */

const DragRotateMode = {
  /**
   * onSetup: initialize mode, create bounding box and corner handles once.
   * @param {Object} opts - contains featureId of polygon to edit
   * @returns {Object} state object for this mode instance
   */

  onSetup({ featureId }) {
    const polygon = this.getFeature(featureId);
    const geoJSON = polygon.toGeoJSON();

    // Build original bounding box: padded bbox + corner handles
    const boundingBox = buildBoundingBox(geoJSON);

    // Create store-features for handles
    const handleSFs = boundingBox.handles.map((hg) => {
      const sf = this.newFeature(hg);
      this.addFeature(sf);
      return sf;
    });

    // Create store-feature for box
    const boxSF = this.newFeature(boundingBox.box);
    this.addFeature(boxSF);

    // Initialize mode state
    const state = {
      polyId: featureId,
      handleIds: handleSFs.map((sf) => sf.id), // IDs of the four corner handles
      boxId: boxSF.id,
      action: null, // 'move' or 'rotate'
      startLngLat: null,
      startAngle: 0,
      origGeom: geoJSON,
      pivot: turf.centroid(geoJSON).geometry.coordinates,
      boundingBox: boundingBox,
      rotation: 0,
    };

    return state;
  },

  /**
   * onMouseDown: determine if user starts move or rotate gesture.
   */
  onMouseDown(state, e) {
    if (e.originalEvent.button !== 0) return; // left click only

    // Recompute pivot from current geometry
    const currentGeo = this.getFeature(state.polyId).toGeoJSON();
    state.pivot = turf.centroid(currentGeo).geometry.coordinates;
    state.origGeom = currentGeo;

    // Rebuild the decoration baseline
    const freshBox = buildBoundingBox(currentGeo);
    state.boundingBox = freshBox;

    // Check if click inside polygon -> move
    if (isInside(currentGeo, e.lngLat)) {
      state.action = "move";
    } else {
      // Else check handles -> rotate
      const hitHandle = state.handleIds.find((id) =>
        isOnFeature(this.getFeature(id).toGeoJSON(), e.lngLat, this.map)
      );
      if (hitHandle) {
        state.action = "rotate";
        state.startAngle = bearingTo(state.pivot, e.lngLat);
      }
    }

    // If action started, disable map pan and record baseline
    if (state.action) {
      this.map.dragPan.disable();
      state.startLngLat = e.lngLat;
    }
  },

  /**
   * onDrag: handle continuous move or rotate.
   */
  onDrag(state, e) {
    if (!state.action) return;

    // MOVE
    if (state.action === "move") {
      const distance = turf.distance(
        turf.point([state.startLngLat.lng, state.startLngLat.lat]),
        turf.point([e.lngLat.lng, e.lngLat.lat]),
        { units: "meters" }
      );
      const brg = bearingTo(state.startLngLat, e.lngLat);
      const moved = turf.transformTranslate(state.origGeom, distance, brg, {
        units: "meters",
      });

      // update polygon
      this.getFeature(state.polyId).setCoordinates(moved.geometry.coordinates);
      // update handles
      state.handleIds.forEach((id, i) => {
        const orig = state.boundingBox.handles[i];
        const trans = turf.transformTranslate(orig, distance, brg, {
          units: "meters",
        });
        this.getFeature(id).setCoordinates(trans.geometry.coordinates);
      });
      // update box
      const boxOrig = state.boundingBox.box;
      const boxTrans = turf.transformTranslate(boxOrig, distance, brg, {
        units: "meters",
      });
      this.getFeature(state.boxId).setCoordinates(
        boxTrans.geometry.coordinates
      );

      this.map.fire("draw.update", {
        action: "move",
        mode: "drag_rotate_handle",
        features: [moved],
      });
    }

    // ROTATE
    if (state.action === "rotate") {
      const angle = bearingTo(state.pivot, e.lngLat) - state.startAngle;
      const current = bearingTo(state.pivot, e.lngLat);
      const delta = shortestDelta(state.startAngle, current);

      const rotated = turf.transformRotate(state.origGeom, delta, {
        pivot: state.pivot,
        mutate: false,
      });
      // update polygon
      const polySF = this.getFeature(state.polyId);
      polySF.setCoordinates(rotated.geometry.coordinates);

      state.rotation = normalizeAngle(state.rotation + delta);

      // update handles
      state.handleIds.forEach((id, i) => {
        const orig = state.boundingBox.handles[i];
        const trans = turf.transformRotate(orig, delta, {
          pivot: state.pivot,
          mutate: false,
        });
        this.getFeature(id).setCoordinates(trans.geometry.coordinates);
      });
      // update box
      const boxOrig = state.boundingBox.box;
      const boxTrans = turf.transformRotate(boxOrig, delta, {
        pivot: state.pivot,
        mutate: false,
      });
      this.getFeature(state.boxId).setCoordinates(
        boxTrans.geometry.coordinates
      );

      this.map.fire("draw.update", {
        action: "rotate",
        mode: "drag_rotate_handle",
        features: [rotated],
      });
    }
  },

  onMouseMove(state, e) {
    // default
    this.map.getCanvas().style.cursor = "";

    // over a rotate handle
    if (
      state.handleIds.some((id) =>
        isOnFeature(this.getFeature(id).toGeoJSON(), e.lngLat, this.map)
      )
    ) {
      this.map.getCanvas().style.cursor = "alias"; // "rotate" ish
      return;
    }

    // inside polygon
    if (isInside(this.getFeature(state.polyId).toGeoJSON(), e.lngLat)) {
      this.map.getCanvas().style.cursor = "move";
    }
  },

  /**
   * onMouseUp: finalize gesture, reset action, update baselines.
   */
  onMouseUp(state) {
    state.action = null;
    this.map.dragPan.enable(); // restore normal map behaviour

    this.map.fire("draw.update", {
      action: "complete",
      mode: "drag_rotate_handle",
      features: [this.getFeature(state.polyId).toGeoJSON()],
    });

    // Reset origGeom to current for next gesture
    const current = this.getFeature(state.polyId).toGeoJSON();
    state.origGeom = current;
    state.pivot = turf.centroid(current).geometry.coordinates;
    state.boundingBox = buildBoundingBox(current);
    
    // store the decoration’s *current* geometry as the new baseline
/*     state.boundingBox.handles.forEach((h, i) => {
      h.geometry.coordinates = this.getFeature(
        state.handleIds[i]
      ).getCoordinates();
    });
    state.boundingBox.box.geometry.coordinates = this.getFeature(
      state.boxId
    ).getCoordinates(); */
  },

  /**
   * onStop: clean up all decoration features.
   */

  onStop(state) {
    [...state.handleIds, state.boxId].forEach((id) => {
      if (this.getFeature(id)) this.deleteFeature(id);
    });
  },

  toDisplayFeatures(state, geojson, push) {
    if (geojson.id === state.polyId) {
      geojson.properties = { ...geojson.properties, active: "true" };
    } else if (state.handleIds.includes(geojson.id)) {
      geojson.properties = {
        ...geojson.properties,
        meta: "rotate_handle",
        active: "true",
      };
    } else if (geojson.id === state.boxId) {
      geojson.properties = { ...geojson.properties, meta: "box" };
    }
    push(geojson);
  },
};

/* ----------------------------- helpers ---------------------------------- */

/**
 * Normalize angle to [0, 360)
 */
function normalizeAngle(a) {
  return ((a % 360) + 360) % 360;
}

/**
 * Compute shortest delta between two angles
 */
function shortestDelta(from, to) {
  let d = ((to - from + 540) % 360) - 180; // –180…+179
  return d === -180 ? 180 : d;
}

/**
 * compute bearing between two points
 */
function bearingTo(a, b) {
  const p1 = Array.isArray(a) ? a : [a.lng, a.lat];
  const p2 = Array.isArray(b) ? b : [b.lng, b.lat];
  return turf.bearing(turf.point(p1), turf.point(p2));
}

/**
 * buildBoundingBox: returns padded bbox and 4 corner handles for a polygon
 */
function buildBoundingBox(polyGeo) {
  // scale the bbox polygon, then pull its corner coords back out
  const padPoly = turf.transformScale(
    turf.bboxPolygon(turf.bbox(polyGeo)),
    1.05 // ← 5 % bigger on each side
  );
  const corners = padPoly.geometry.coordinates[0].slice(0, 4);

  const handles = corners.map((c, i) => ({
    type: "Feature",
    properties: { meta: "rotate_handle", corner: i },
    geometry: { type: "Point", coordinates: c },
  }));

  const box = {
    type: "Feature",
    properties: { meta: "box" },
    geometry: { type: "LineString", coordinates: [...corners, corners[0]] },
  };
  return { handles, box };
}

/**
 * check if lngLat hits a point feature within tolerance
 */

function isOnFeature(featureGeo, lngLat, map) {
  const point = map.project(lngLat);
  const tol = 10;
  return turf.explode(featureGeo).features.some((pt) => {
    const p = map.project(pt.geometry.coordinates);
    return Math.hypot(p.x - point.x, p.y - point.y) < tol;
  });
}

/**
 * point-in-polygon test
 */
function isInside(featureGeo, lngLat) {
  return turf.booleanPointInPolygon(
    turf.point([lngLat.lng, lngLat.lat]),
    featureGeo
  );
}


export default DragRotateMode;
